import React, { useMemo } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { DiscountTypes } from 'api';
import { discountLabels, getInitialMetaFieldValue } from '../fields';

interface Props {
  discountTypes?: DiscountTypes.DiscountTypeItems;
  watch: UseFormWatch<DiscountTypes.ItemCreateParams>;
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
  onChange: (value: DiscountTypes.DiscountTypeItem | undefined) => void;
}

const Index = (props: Props): JSX.Element | null => {
  const { setValue, watch, onChange, discountTypes } = props;

  const appDescription = watch('app_description');

  const handleClick = (value: DiscountTypes.DiscountTypeItem | undefined) => {
    if (value) {
      const initialValue = getInitialMetaFieldValue(value.namespace);

      if (initialValue) {
        setValue('app_function_id', value.app_function_id);
        setValue('app_description', value.app_description);
        setValue('metafields_attributes', [
          {
            namespace: value.namespace,
            type: 'json',
            key: 'discount',
            value: initialValue,
          },
        ]);
      } else {
        setValue('app_function_id', undefined);
        setValue('app_description', undefined);
        setValue(`metafields_attributes`, undefined);
        onChange(undefined);
      }

      onChange(value);
    } else {
      setValue('app_function_id', undefined);
      setValue('app_description', undefined);
      setValue(`metafields_attributes`, undefined);
      onChange(undefined);
    }
  };

  const label = useMemo(() => {
    const namespace = discountTypes?.find(
      (type) => type.app_description === appDescription,
    )?.namespace;

    if (namespace) {
      return discountLabels[namespace];
    }

    return appDescription || 'Default discount';
  }, [appDescription, discountTypes]);

  return (
    <Stack spacing={2}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle1">
          Current discount type is{' '}
          <Typography
            variant="inherit"
            color="primary.main"
            fontWeight="bold"
            component="span"
          >
            {label}
          </Typography>
        </Typography>
      </Box>
      {!discountTypes && (
        <>
          <Skeleton variant="rounded" height="36px" width="100%" />
          <Skeleton variant="rounded" height="36px" width="100%" />
          <Skeleton variant="rounded" height="36px" width="100%" />
        </>
      )}
      {discountTypes && (
        <Button
          fullWidth
          onClick={() => handleClick(undefined)}
          disabled={!appDescription}
        >
          Default discount
        </Button>
      )}
      {discountTypes &&
        discountTypes.map((item) => (
          <Button
            key={item.app_function_id}
            disabled={appDescription === item.app_function_id}
            onClick={() => handleClick(item)}
          >
            {item.namespace in discountLabels
              ? discountLabels[item.namespace]
              : item.app_description}
          </Button>
        ))}
    </Stack>
  );
};

export default Index;
